import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute, RequireAuthNoMembership, PublicOnlyRoute } from './auth/protected-route.js';
import { AuthCallbackPage } from './auth/auth-callback-page.js';
import { useAuth } from './auth/use-auth.js';
import { AppShell } from './shell/app-shell.js';
import { AccountPage } from './pages/account-page.js';
import { StudioSettingsPage } from './pages/studio-settings-page.js';
import { FeedbackPage } from './pages/feedback-page.js';
import { FeedbackSettingsPage } from './pages/feedback-settings/feedback-settings-page.js';
import { FormBuilderPage } from './pages/feedback-settings/form-builder-page.js';
import { FormResponsesPage } from './pages/feedback-settings/responses/responses-page.js';
import { FormsSection } from './pages/feedback-settings/forms-section.js';
import { InsightsSection } from './pages/feedback-settings/insights-section.js';
import { WatchedChannelsPage } from './pages/feedback-settings/watched-channels-page.js';
import { ForgotPasswordPage } from './pages/forgot-password-page.js';
import { GameSection } from './pages/game-section.js';
import { GameSettingsLayout } from './pages/game-settings/game-settings-layout.js';
import { GameSettingsBuildsPage } from './pages/game-settings/builds-page.js';
import { GameSettingsDiscordPage } from './pages/game-settings/discord-page.js';
import { GameSettingsMilestonesPage } from './pages/game-settings/milestones-page.js';
import { GameSettingsNotificationsPage } from './pages/game-settings/notifications-page.js';
import { GameSettingsOverviewPage } from './pages/game-settings/overview-page.js';
import { ImprovementLogPage } from './pages/improvement-log-page.js';
import { InsightsPage } from './pages/insights-page.js';
import { InsightsChannelsPage } from './insights/index.js';
import { InviteAcceptPage } from './pages/invite-accept-page.js';
import { NewGamePage } from './pages/new-game-page.js';
import { NewStudioPage } from './pages/new-studio-page.js';
import { NotFoundPage } from './pages/not-found-page.js';
import { OAuthDiscordCallbackPage } from './pages/oauth-discord-callback-page.js';
import { OverviewPage } from './pages/overview-page.js';
import { PlaytestsSectionPage } from './pages/playtests/playtests-section-page.js';
import { PlaytestSettingsPage } from './pages/playtest-settings/playtest-settings-page.js';
import { PlaytestsSection } from './pages/playtest-settings/flows/playtests-section.js';
import { PlaytestBuilderPage } from './pages/playtest-settings/flows/builder/playtest-builder-page.js';
import { PlaytestRecruitmentPage } from './pages/playtest-settings/recruitment/recruitment-page.js';
import { PlaytestPairingIntegrationPage } from './pages/playtest-settings/pairing-integration/pairing-integration-page.js';
import { ResetPasswordPage } from './pages/reset-password-page.js';
import { SignInPage } from './pages/sign-in-page.js';
import { SignUpPage } from './pages/sign-up-page.js';
import { StudioRootPage } from './pages/studio-root-page.js';
import { WelcomeDiscordPage } from './pages/welcome-discord-page.js';
import { WelcomePage } from './pages/welcome-page.js';

// `/` resolves to the user's first studio so deep-linking works on a clean
// session. Auth-related redirects (loading / unauthenticated / no membership)
// are handled by the existing route guards.
function RootRedirect(): JSX.Element {
  const { studios, activeStudioId } = useAuth();
  const target =
    studios.find((s) => s.id === activeStudioId) ?? studios[0];
  if (target === undefined) return <Navigate to="/welcome" replace />;
  return <Navigate to={`/s/${target.shortId}`} replace />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

      <Route path="/welcome" element={<RequireAuthNoMembership><WelcomePage /></RequireAuthNoMembership>} />
      <Route path="/studios/new" element={<ProtectedRoute><NewStudioPage /></ProtectedRoute>} />
      <Route path="/sign-in" element={<PublicOnlyRoute><SignInPage /></PublicOnlyRoute>} />
      <Route path="/sign-up" element={<PublicOnlyRoute><SignUpPage /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Discord bot-install OAuth redirect lands here. Auth-gated because
          the callback resolves the originating studio from the user's
          memberships, but it has no studio guard - the OAuth state row
          carries the originating studio. */}
      <Route
        path="/oauth/discord-callback"
        element={
          <ProtectedRoute>
            <OAuthDiscordCallbackPage />
          </ProtectedRoute>
        }
      />
      {/* Invite acceptance is a public route - works logged in or out. */}
      <Route path="/invite/:token" element={<InviteAcceptPage />} />

      <Route path="/s/:studioShortId" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<StudioRootPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="studio-settings" element={<StudioSettingsPage />} />
        <Route path="games/new" element={<NewGamePage />} />

        <Route path="g/:gameShortId" element={<GameSection />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="welcome-discord" element={<WelcomeDiscordPage />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="insights/channels" element={<InsightsChannelsPage />} />
          <Route path="playtests" element={<PlaytestsSectionPage />}>
            <Route index element={null} />
            <Route path=":playtestId" element={null} />
            <Route path=":playtestId/sessions/:sessionId" element={null} />
          </Route>
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="improvement-log" element={<ImprovementLogPage />} />
          <Route path="playtest-settings" element={<PlaytestSettingsPage />}>
            <Route index element={<Navigate to="flows" replace />} />
            <Route path="recruitment" element={<PlaytestRecruitmentPage />} />
            <Route path="pairing-integration" element={<PlaytestPairingIntegrationPage />} />
            <Route path="flows" element={<PlaytestsSection />} />
            <Route path="flows/:playtestId" element={<PlaytestBuilderPage />} />
          </Route>
          <Route path="feedback-settings" element={<FeedbackSettingsPage />}>
            <Route index element={<Navigate to="watched-channels" replace />} />
            <Route path="watched-channels" element={<WatchedChannelsPage />} />
            <Route path="forms" element={<FormsSection />} />
            <Route path="forms/:formId" element={<FormBuilderPage />} />
            <Route path="forms/:formId/responses" element={<FormResponsesPage />} />
            <Route path="insights" element={<InsightsSection />} />
          </Route>
          <Route path="game-settings" element={<GameSettingsLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<GameSettingsOverviewPage />} />
            <Route path="builds" element={<GameSettingsBuildsPage />} />
            <Route path="milestones" element={<GameSettingsMilestonesPage />} />
            <Route path="discord" element={<GameSettingsDiscordPage />} />
            <Route path="notifications" element={<GameSettingsNotificationsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
